
import Vue from "vue";
import {
  DynamicPgFeeSettings,
  OrderPathItem,
  PaymentMethodItem,
  UnconfiguredComboItem,
  DEFAULT_DYNAMIC_PG_FEE_SETTINGS,
  loadPgFeeRates,
  savePgFeeRatesToServer,
  exportPgFeeCsv,
  importPgFeeCsv,
} from "@/utils/pgFee";
import { readFileAsText } from "@/utils/csv";
import eventBus from "@/eventBus";
import {
  ORDER_PATH_DEFINITIONS,
  OWNED_MALL_ORDER_PATH_GROUP,
  OrderPathPresentationMode,
} from "@/utils/orderPathFilter";

type DisplayOrderPathItem = OrderPathItem & {
  isGroup?: boolean;
  isConfigured?: boolean;
  displayCode?: string;
};

export default Vue.extend({
  name: "PgFeeSettingsModal",
  data() {
    return {
      dialog: false,
      saving: false,
      currentTab: "matrix", // 'matrix' | 'paths' | 'methods' | 'unconfigured'
      showAddPathModal: false,
      showAddMethodModal: false,
      newPathName: "",
      newPathCode: "",
      newMethodName: "",
      newMethodDefaultRate: 2.2,
      unconfiguredList: [] as UnconfiguredComboItem[],
      unconfiguredInputs: {} as Record<string, number>,
      orderPathPresentationMode: "grouped" as OrderPathPresentationMode,
      form: {
        vatIncluded: true,
        globalDefaultRate: 2.2,
        orderPaths: [],
        paymentMethods: [],
        rules: [],
      } as DynamicPgFeeSettings,
    };
  },
  watch: {
    currentTab(val) {
      if (this.dialog) {
        this.$emit("tab-change", val);
      }
    },
  },
  computed: {
    visibleOrderPaths(): DisplayOrderPathItem[] {
      const configuredByFeeCode = new Map(
        ORDER_PATH_DEFINITIONS.map((definition) => [
          definition.feePathCode.toLowerCase(),
          definition,
        ])
      );
      if (this.orderPathPresentationMode !== "grouped") {
        return this.form.orderPaths.map((path) => {
          const definition = configuredByFeeCode.get(path.code.toLowerCase());
          return definition
            ? { ...path, name: definition.label, isConfigured: true }
            : path;
        });
      }
      const sourceDefinition = ORDER_PATH_DEFINITIONS.find(
        (definition) =>
          definition.id === OWNED_MALL_ORDER_PATH_GROUP.feeSourceId
      );
      const groupedFeeCode = sourceDefinition?.feePathCode || "cafe24";
      const groupedCodes = new Set(
        ORDER_PATH_DEFINITIONS.filter((definition) =>
          OWNED_MALL_ORDER_PATH_GROUP.memberIds.includes(definition.id)
        ).map((definition) => definition.feePathCode.toLowerCase())
      );
      const otherPaths = this.form.orderPaths.filter(
        (path) => !groupedCodes.has(path.code.toLowerCase())
      );
      return [
        {
          code: groupedFeeCode,
          name: OWNED_MALL_ORDER_PATH_GROUP.label,
          isSystem: true,
          isGroup: true,
          displayCode: OWNED_MALL_ORDER_PATH_GROUP.memberIds.join(", "),
        },
        ...otherPaths,
      ];
    },
  },
  methods: {
    downloadCsv() {
      try {
        exportPgFeeCsv(this.form, this.orderPathPresentationMode);
        eventBus.$emit("snackbar", {
          message: "📥 수수료율 설정 엑셀 파일 다운로드가 완료되었습니다.",
        });
      } catch (e) {
        console.error("Excel export failed", e);
        eventBus.$emit("snackbar", {
          message: "❌ 엑셀 파일 다운로드에 실패했습니다.",
        });
      }
    },
    triggerCsvUpload() {
      const input = this.$refs.csvFileInput as HTMLInputElement;
      if (input) {
        input.value = "";
        input.click();
      }
    },
    async handleCsvUpload(e: Event) {
      const input = e.target as HTMLInputElement;
      if (!input || !input.files || input.files.length === 0) return;

      const file = input.files[0];
      try {
        const text = await readFileAsText(file);
        const result = importPgFeeCsv(text, this.form);

        this.form = result.settings;

        let msg = `✅ 엑셀 파일에서 ${result.updatedCount}개의 수수료율 설정이 일괄 적용되었습니다.`;
        if (result.addedPaths > 0 || result.addedMethods > 0) {
          msg += ` (신규 주문경로 ${result.addedPaths}개, 결제수단 ${result.addedMethods}개 추가됨)`;
        }

        eventBus.$emit("snackbar", { message: msg });
      } catch (err: any) {
        console.error("Excel import failed", err);
        eventBus.$emit("snackbar", {
          message: `❌ 엑셀 업로드 오류: ${
            err?.message || "파일 형식이 올바르지 않습니다."
          }`,
        });
      }
    },
    open(
      unconfiguredItems?: UnconfiguredComboItem[],
      targetTab?: string,
      presentationMode?: OrderPathPresentationMode
    ) {
      const isAlreadyOpen = this.dialog;
      this.form = loadPgFeeRates();
      if (presentationMode) {
        this.orderPathPresentationMode = presentationMode;
      }
      if (Array.isArray(unconfiguredItems)) {
        this.unconfiguredList = unconfiguredItems;
      } else if (!isAlreadyOpen) {
        // Route synchronization can reopen the visible modal without payload.
        // Preserve the combinations received from the original banner click.
        this.unconfiguredList = [];
      }
      this.currentTab =
        targetTab ||
        (this.unconfiguredList.length > 0 ? "unconfigured" : "matrix");
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.$emit("close");
    },
    closeSilently() {
      this.dialog = false;
    },
    resetDefault() {
      this.form = JSON.parse(JSON.stringify(DEFAULT_DYNAMIC_PG_FEE_SETTINGS));
      eventBus.$emit("snackbar", {
        message: "🔄 기본 수수료율 설정으로 복원되었습니다.",
      });
    },

    // Rule Rate helpers for Matrix Table
    getRuleRate(
      path: OrderPathItem,
      method: PaymentMethodItem
    ): string | number {
      const rule = this.form.rules.find(
        (r) =>
          (r.pathCodeOrName.toLowerCase() === path.code.toLowerCase() ||
            r.pathCodeOrName.toLowerCase() === path.name.toLowerCase()) &&
          (r.methodCodeOrName.toLowerCase() === method.code.toLowerCase() ||
            r.methodCodeOrName.toLowerCase() === method.name.toLowerCase())
      );
      if (rule && typeof rule.rate === "number") {
        return rule.rate;
      }
      return "";
    },
    updateRuleRate(
      path: OrderPathItem,
      method: PaymentMethodItem,
      valStr: string
    ) {
      const val = parseFloat(valStr);
      const existingIdx = this.form.rules.findIndex(
        (r) =>
          (r.pathCodeOrName.toLowerCase() === path.code.toLowerCase() ||
            r.pathCodeOrName.toLowerCase() === path.name.toLowerCase()) &&
          (r.methodCodeOrName.toLowerCase() === method.code.toLowerCase() ||
            r.methodCodeOrName.toLowerCase() === method.name.toLowerCase())
      );

      if (isNaN(val) || valStr === "") {
        if (existingIdx !== -1) {
          this.form.rules.splice(existingIdx, 1);
        }
      } else {
        if (existingIdx !== -1) {
          this.form.rules[existingIdx].rate = val;
        } else {
          this.form.rules.push({
            pathCodeOrName: path.code,
            methodCodeOrName: method.code,
            rate: val,
          });
        }
      }
    },

    // Order Path Management
    openAddPathModal() {
      this.newPathName = "";
      this.newPathCode = "";
      this.showAddPathModal = true;
    },
    confirmAddPath() {
      const name = this.newPathName.trim();
      if (!name) return;

      const code = this.newPathCode.trim() || `custom_path_${Date.now()}`;
      const exists = this.form.orderPaths.some(
        (p) =>
          p.name.toLowerCase() === name.toLowerCase() ||
          p.code.toLowerCase() === code.toLowerCase()
      );

      if (exists) {
        eventBus.$emit("snackbar", {
          message: `⚠️ '${name}' 주문경로가 이미 존재합니다.`,
        });
        return;
      }

      this.form.orderPaths.push({ code, name, isSystem: false });
      this.showAddPathModal = false;
      this.newPathName = "";
      this.newPathCode = "";
    },
    removePath(idx: number) {
      if (idx >= 0 && idx < this.form.orderPaths.length) {
        const target = this.form.orderPaths[idx];
        if (target.isSystem) return;
        this.form.orderPaths.splice(idx, 1);
      }
    },
    removePathByCode(code: string) {
      const idx = this.form.orderPaths.findIndex((path) => path.code === code);
      this.removePath(idx);
    },

    // Payment Method Management
    openAddMethodModal() {
      this.newMethodName = "";
      this.newMethodDefaultRate = 2.2;
      this.showAddMethodModal = true;
    },
    confirmAddMethod() {
      const name = this.newMethodName.trim();
      if (!name) return;

      const code = `custom_method_${Date.now()}`;
      const exists = this.form.paymentMethods.some(
        (m) => m.name.toLowerCase() === name.toLowerCase()
      );

      if (exists) {
        eventBus.$emit("snackbar", {
          message: `⚠️ '${name}' 결제수단이 이미 존재합니다.`,
        });
        return;
      }

      this.form.paymentMethods.push({
        code,
        name,
        defaultRate: this.newMethodDefaultRate ?? 2.2,
        isSystem: false,
      });
      this.showAddMethodModal = false;
      this.newMethodName = "";
    },
    removeMethod(idx: number) {
      if (idx >= 0 && idx < this.form.paymentMethods.length) {
        const target = this.form.paymentMethods[idx];
        if (target.isSystem) return;
        this.form.paymentMethods.splice(idx, 1);
      }
    },

    // Unconfigured Item Resolution
    getUnconfiguredInputRate(item: UnconfiguredComboItem): number {
      const key = `${item.pathCode}:${item.methodCode}`;
      if (typeof this.unconfiguredInputs[key] === "number") {
        return this.unconfiguredInputs[key];
      }
      return 2.2;
    },
    setUnconfiguredInputRate(item: UnconfiguredComboItem, valStr: string) {
      const key = `${item.pathCode}:${item.methodCode}`;
      const val = parseFloat(valStr);
      this.$set(this.unconfiguredInputs, key, isNaN(val) ? 0 : val);
    },
    applyUnconfiguredRule(item: UnconfiguredComboItem) {
      const rate = this.getUnconfiguredInputRate(item);
      this.updateRuleRate(
        { code: item.pathCode, name: item.pathName },
        { code: item.methodCode, name: item.methodName, defaultRate: 2.2 },
        String(rate)
      );

      this.unconfiguredList = this.unconfiguredList.filter(
        (u) =>
          !(u.pathCode === item.pathCode && u.methodCode === item.methodCode)
      );

      eventBus.$emit("snackbar", {
        message: `✅ [${item.pathName} x ${item.methodName}] 수수료율 ${rate}% 등록되었습니다.`,
      });

      if (this.unconfiguredList.length === 0) {
        this.currentTab = "matrix";
      }
    },

    async save() {
      this.saving = true;
      try {
        const success = await savePgFeeRatesToServer(this.form);
        if (success) {
          this.dialog = false;
          eventBus.$emit("snackbar", {
            message: "✅ 수수료율 설정이 성공적으로 저장되었습니다.",
          });
          eventBus.$emit("pg-fee-updated");
        } else {
          eventBus.$emit("snackbar", {
            message:
              "⚠️ 서버 저장에 실패했습니다. 수정 내용은 저장하지 않았습니다. 연결을 확인한 뒤 다시 시도해주세요.",
          });
        }
      } finally {
        this.saving = false;
      }
    },
    updateOrderPathPresentationMode(mode: OrderPathPresentationMode) {
      this.orderPathPresentationMode = mode;
    },
  },
  created() {
    eventBus.$on(
      "order-path-presentation-mode-updated",
      this.updateOrderPathPresentationMode
    );
  },
  beforeDestroy() {
    eventBus.$off(
      "order-path-presentation-mode-updated",
      this.updateOrderPathPresentationMode
    );
  },
});
